import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Loader2, Check, Trash2, Plus, MoreVertical, ChevronDown, ChevronRight,
  Share2, Copy, MessageCircle, Mail, Download, GripVertical, ArrowUp, ArrowDown, ShoppingCart,
  Sparkles, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AppHeader } from "@/components/AppHeader";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";
import { useSubscription } from "@/hooks/useSubscription";
import { DEPARTMENT_ORDER, type Department } from "@/lib/departments";
import { canonicalKey, cryptoRandomId, type ShoppingItem } from "@/lib/shoppingList";
import { formatShoppingListText, formatShoppingListForSearch, slugifyForFilename } from "@/lib/shoppingListExport";
import {
  setDepartmentOverride,
  removeDepartmentOverride,
  resetDepartmentOverrides,
} from "@/lib/departmentOverrides";
import {
  getUserShoppingPrefs,
  saveDepartmentOrder,
  resetDepartmentOrder,
  recordCorrection,
  suggestionForKey,
  effectiveDepartmentOrder,
  type UserShoppingPrefs,
} from "@/lib/userShoppingPrefs";
import { EmptyStateMotif } from "@/components/EmptyStateMotif";

const ShoppingList = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { email, loading: subLoading } = useSubscription();
  const [menuTitle, setMenuTitle] = useState("");
  const [items, setItems] = useState<ShoppingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [showChecked, setShowChecked] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [newItemDept, setNewItemDept] = useState<Department>("Household / Other");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editQty, setEditQty] = useState("");
  // User's saved shopping prefs (department order + correction history).
  const [prefs, setPrefs] = useState<UserShoppingPrefs>({
    departmentOrder: [],
    correctionHistory: [],
    defaultMealSlots: ["breakfast", "lunch", "dinner"],
  });
  // Local working copy of department order while in reorder mode.
  const [reorderMode, setReorderMode] = useState(false);
  const [draftOrder, setDraftOrder] = useState<Department[]>([]);
  // Active suggestion (one at a time) for "Move X to Y like last time?"
  const [suggestion, setSuggestion] = useState<
    | { itemId: string; name: string; from: Department; to: Department }
    | null
  >(null);
  const dismissedSuggestions = useRef<Set<string>>(new Set());

  // Debounced autosave: every change to `items` flushes to the DB after a
  // short pause. We never block the UI on saves.
  const saveTimer = useRef<number | null>(null);
  const skipNextSave = useRef(true);

  useEffect(() => {
    if (subLoading) return;
    if (!email) {
      navigate("/my-recipes");
      return;
    }
    if (!id) return;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, email, subLoading]);

  const load = async () => {
    if (!id || !email) return;
    setLoading(true);
    const [menuRes, listRes, prefsRes] = await Promise.all([
      supabase.from("menus").select("title").eq("id", id).maybeSingle(),
      supabase.from("shopping_lists").select("items").eq("menu_id", id).maybeSingle(),
      getUserShoppingPrefs(email),
    ]);
    if (menuRes.error || !menuRes.data) {
      toast.error("Menu not found");
      navigate("/menus");
      return;
    }
    if (!listRes.data) {
      toast.error("Generate the shopping list first");
      navigate(`/menus/${id}`);
      return;
    }
    setMenuTitle(menuRes.data.title);
    skipNextSave.current = true; // first set on load shouldn't trigger save
    setItems((listRes.data.items as unknown as ShoppingItem[]) || []);
    setPrefs(prefsRes);
    setLoading(false);
  };

  // Debounced autosave on every items change.
  useEffect(() => {
    if (loading) return;
    if (skipNextSave.current) {
      skipNextSave.current = false;
      return;
    }
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(async () => {
      if (!id) return;
      const { error } = await supabase
        .from("shopping_lists")
        .update({ items: items as unknown as never })
        .eq("menu_id", id);
      if (error) {
        console.error(error);
        toast.error("Couldn't save changes");
      }
    }, 600);
    return () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
  }, [items, id, loading]);

  const orderedDepartments = useMemo(
    () => effectiveDepartmentOrder(prefs),
    [prefs],
  );

  const grouped = useMemo(() => {
    const map = new Map<Department, ShoppingItem[]>();
    orderedDepartments.forEach((d) => map.set(d, []));
    for (const it of items) {
      if (it.checked) continue;
      const list = map.get(it.department) || map.set(it.department, []).get(it.department)!;
      list.push(it);
    }
    return map;
  }, [items, orderedDepartments]);

  const checkedItems = useMemo(() => items.filter((i) => i.checked), [items]);

  const totalRemaining = items.length - checkedItems.length;

  const updateItem = useCallback((itemId: string, patch: Partial<ShoppingItem>) => {
    setItems((prev) => prev.map((it) => (it.id === itemId ? { ...it, ...patch } : it)));
  }, []);

  const deleteItem = useCallback((itemId: string) => {
    setItems((prev) => prev.filter((it) => it.id !== itemId));
  }, []);

  const moveItem = useCallback((itemId: string, dept: Department) => {
    const target = items.find((it) => it.id === itemId);
    updateItem(itemId, { department: dept });
    if (!target || !email || target.custom) return;
    const key = canonicalKey(target.name);
    if (!key) return;
    const previousDept = target.department;
    if (previousDept === dept) return;
    void (async () => {
      try {
        await setDepartmentOverride(email, key, dept);
        // Record correction in user prefs (powers adaptive suggestions).
        await recordCorrection(email, { key, from: previousDept, to: dept });
        // Refresh local prefs cache so future suggestions see this entry.
        const next = await getUserShoppingPrefs(email);
        setPrefs(next);
        toast.success("Saved — we'll remember this", {
          action: {
            label: "Undo",
            onClick: () => {
              void (async () => {
                try {
                  await removeDepartmentOverride(email, key);
                  updateItem(itemId, { department: previousDept });
                } catch {
                  /* toast already logged */
                }
              })();
            },
          },
        });
      } catch {
        toast.error("Couldn't remember that change");
      }
    })();
  }, [items, email, updateItem]);

  // Adaptive suggestion: when a new list loads, scan items for ones whose
  // canonical key has been corrected to a consistent department ≥ 2 times
  // in the past. Surface ONE at a time, dismissible, never blocking.
  useEffect(() => {
    if (loading || reorderMode || suggestion) return;
    for (const it of items) {
      if (it.checked || it.custom) continue;
      const key = canonicalKey(it.name);
      if (!key) continue;
      if (dismissedSuggestions.current.has(`${it.id}:${key}`)) continue;
      const target = suggestionForKey(prefs, key, it.department);
      if (target) {
        setSuggestion({ itemId: it.id, name: it.name, from: it.department, to: target });
        return;
      }
    }
  }, [items, prefs, loading, reorderMode, suggestion]);

  const acceptSuggestion = useCallback(() => {
    if (!suggestion) return;
    moveItem(suggestion.itemId, suggestion.to);
    setSuggestion(null);
  }, [suggestion, moveItem]);

  const dismissSuggestion = useCallback(() => {
    if (!suggestion) return;
    const key = canonicalKey(suggestion.name);
    dismissedSuggestions.current.add(`${suggestion.itemId}:${key}`);
    setSuggestion(null);
  }, [suggestion]);

  // ---------- Reorder mode ----------
  const enterReorder = useCallback(() => {
    setDraftOrder([...orderedDepartments]);
    setReorderMode(true);
  }, [orderedDepartments]);

  const moveDept = useCallback((dept: Department, dir: -1 | 1) => {
    setDraftOrder((prev) => {
      const idx = prev.indexOf(dept);
      if (idx < 0) return prev;
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return next;
    });
  }, []);

  const saveReorder = useCallback(() => {
    if (!email) return;
    void (async () => {
      try {
        await saveDepartmentOrder(email, draftOrder);
        setPrefs((p) => ({ ...p, departmentOrder: draftOrder }));
        setReorderMode(false);
        toast.success("Aisle order saved");
      } catch {
        toast.error("Couldn't save order");
      }
    })();
  }, [email, draftOrder]);

  const cancelReorder = useCallback(() => {
    setReorderMode(false);
    setDraftOrder([]);
  }, []);

  const resetReorderToDefault = useCallback(() => {
    setDraftOrder([...DEPARTMENT_ORDER]);
  }, []);

  const handleResetOverrides = useCallback(() => {
    if (!email) return;
    if (!window.confirm("Reset all learned categories? Future shopping lists will use the defaults.")) {
      return;
    }
    void (async () => {
      try {
        await resetDepartmentOverrides(email);
        await resetDepartmentOrder(email);
        setPrefs((p) => ({ ...p, departmentOrder: [], correctionHistory: [] }));
        toast.success("Learned categories reset");
      } catch {
        toast.error("Couldn't reset learned categories");
      }
    })();
  }, [email]);

  const addCustomItem = () => {
    const name = newItemName.trim();
    if (!name) return;
    const item: ShoppingItem = {
      id: cryptoRandomId(),
      name,
      quantity: "",
      unit: "",
      note: "",
      department: newItemDept,
      checked: false,
      position: items.length,
      custom: true,
      sources: [],
    };
    setItems((prev) => [...prev, item]);
    setNewItemName("");
  };

  const startEdit = (it: ShoppingItem) => {
    setEditingId(it.id);
    setEditName(it.name);
    setEditQty([it.quantity, it.unit].filter(Boolean).join(" "));
  };

  const saveEdit = () => {
    if (!editingId) return;
    // Naive split: first token if numeric-ish counts as quantity, rest as unit/name
    const trimmed = editQty.trim();
    let quantity = "";
    let unit = "";
    if (trimmed) {
      const m = trimmed.match(/^(\S+)(?:\s+(.+))?$/);
      if (m) {
        quantity = m[1];
        unit = m[2] || "";
      }
    }
    updateItem(editingId, { name: editName.trim() || "(unnamed)", quantity, unit });
    setEditingId(null);
  };

  const toggleCollapsed = (dept: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(dept)) next.delete(dept);
      else next.add(dept);
      return next;
    });
  };

  const moveWithin = (dept: Department, idx: number, dir: -1 | 1) => {
    const inDept = grouped.get(dept) || [];
    const target = inDept[idx + dir];
    if (!target) return;
    const current = inDept[idx];
    setItems((prev) => {
      // Swap position fields between current and target items
      return prev.map((it) => {
        if (it.id === current.id) return { ...it, position: target.position };
        if (it.id === target.id) return { ...it, position: current.position };
        return it;
      });
    });
  };

  // ---------- Share / Export ----------
  const uncheckedCount = useMemo(
    () => items.filter((it) => !it.checked).length,
    [items],
  );

  const buildShareText = useCallback(
    () => formatShoppingListText(menuTitle, items, { includeChecked: false }),
    [menuTitle, items],
  );

  const handleNativeShare = useCallback(async () => {
    const text = buildShareText();
    const title = `${menuTitle || "Shopping list"} — Shopping list`;
    try {
      if (
        typeof navigator !== "undefined" &&
        typeof navigator.share === "function"
      ) {
        await navigator.share({ title, text });
        return true;
      }
    } catch (err: unknown) {
      const name = (err as { name?: string })?.name;
      if (name === "AbortError") return true;
    }
    return false;
  }, [buildShareText, menuTitle]);

  const copyToClipboard = useCallback(async (text: string): Promise<boolean> => {
    // Try the modern API first (works on desktop + Android Chrome).
    try {
      if (
        typeof navigator !== "undefined" &&
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === "function" &&
        window.isSecureContext
      ) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // fall through to legacy fallback (iOS Safari in some contexts, or
      // when the Radix dropdown dismissal breaks the user-gesture chain).
    }
    // Legacy fallback — works on iOS Safari + older browsers.
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.top = "0";
      ta.style.left = "0";
      ta.style.opacity = "0";
      ta.style.pointerEvents = "none";
      document.body.appendChild(ta);
      // iOS needs the element to be visible-ish and selected via Range.
      const range = document.createRange();
      range.selectNodeContents(ta);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
      ta.setSelectionRange(0, text.length);
      const ok = document.execCommand("copy");
      sel?.removeAllRanges();
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }, []);

  const handleCopy = useCallback(async () => {
    const text = buildShareText();
    const ok = await copyToClipboard(text);
    if (ok) toast.success("Shopping list copied");
    else toast.error("Couldn't copy");
  }, [buildShareText, copyToClipboard]);

  const handleCopyForSearch = useCallback(async () => {
    const text = formatShoppingListForSearch(items, { includeChecked: false });
    if (!text) {
      toast.info("Nothing to copy — all items are ticked off");
      return;
    }
    const count = text.split("\n").length;
    const ok = await copyToClipboard(text);
    if (ok) {
      toast.success(`Copied ${count} item${count === 1 ? "" : "s"}`, {
        description: "Paste into your supermarket search — quantities & aisles stripped",
      });
    } else {
      toast.error("Couldn't copy");
    }
  }, [items, copyToClipboard]);

  const handleWhatsApp = useCallback(() => {
    const text = buildShareText();
    window.open(
      `https://wa.me/?text=${encodeURIComponent(text)}`,
      "_blank",
      "noopener,noreferrer",
    );
  }, [buildShareText]);

  const handleEmail = useCallback(() => {
    const text = buildShareText();
    const subject = `${menuTitle || "Shopping list"} — Shopping list`;
    window.location.href = `mailto:?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(text)}`;
  }, [buildShareText, menuTitle]);

  const handleDownload = useCallback(() => {
    const text = buildShareText();
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${slugifyForFilename(menuTitle)}-shopping-list.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [buildShareText, menuTitle]);

  const [hasNativeShare, setHasNativeShare] = useState(false);
  useEffect(() => {
    setHasNativeShare(
      typeof navigator !== "undefined" && typeof navigator.share === "function",
    );
  }, []);

  const onShareClick = useCallback(async () => {
    if (uncheckedCount === 0) {
      toast.info("Nothing to share — all items are ticked off");
      return;
    }
    if (hasNativeShare) {
      const ok = await handleNativeShare();
      if (ok) return;
    }
    // Fallback: copy as the simplest universal action
    await handleCopy();
  }, [hasNativeShare, handleNativeShare, handleCopy, uncheckedCount]);


  return (
    <main id="main" className="min-h-screen bg-warm-gradient">
      <AppHeader back={{ to: `/menus/${id}`, label: "Back to menu" }} />

      <section className="mx-auto max-w-3xl px-6 pb-32 pt-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="mb-6 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Shopping list
                </p>
                <h1 className="mt-1 truncate font-display text-3xl font-semibold leading-tight text-foreground sm:text-4xl">
                  {menuTitle}
                </h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  {totalRemaining} item{totalRemaining === 1 ? "" : "s"} to buy
                  {checkedItems.length > 0 && ` · ${checkedItems.length} ticked off`}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={onShareClick}
                  disabled={uncheckedCount === 0}
                  className="gap-1.5"
                  aria-label="Share shopping list"
                >
                  <Share2 className="h-4 w-4" />
                  <span className="hidden sm:inline">Share</span>
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      aria-label="More options"
                      className="px-2"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    <DropdownMenuItem onClick={handleCopy} disabled={uncheckedCount === 0}>
                      <Copy className="mr-2 h-4 w-4" />
                      Copy to clipboard
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleCopyForSearch}
                      disabled={uncheckedCount === 0}
                      className="flex-col items-start gap-0.5"
                    >
                      <div className="flex items-center">
                        <ShoppingCart className="mr-2 h-4 w-4" />
                        Copy for online shop
                      </div>
                      <span className="ml-6 text-[11px] text-muted-foreground">
                        Sainsbury's, Tesco, Ocado…
                      </span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleWhatsApp} disabled={uncheckedCount === 0}>
                      <MessageCircle className="mr-2 h-4 w-4" />
                      WhatsApp
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleEmail} disabled={uncheckedCount === 0}>
                      <Mail className="mr-2 h-4 w-4" />
                      Email
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleDownload} disabled={uncheckedCount === 0}>
                      <Download className="mr-2 h-4 w-4" />
                      Download .txt
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={enterReorder}>
                      <GripVertical className="mr-2 h-4 w-4" />
                      Reorder aisles
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Adaptive suggestion: dismissible, never blocks */}
            {suggestion && !reorderMode && (
              <div className="mb-4 flex items-start gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm">
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div className="flex-1">
                  <p className="text-foreground">
                    Move <span className="font-medium">{suggestion.name}</span> to{" "}
                    <span className="font-medium">{suggestion.to}</span> like last time?
                  </p>
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" variant="default" onClick={acceptSuggestion}>
                      Yes, move it
                    </Button>
                    <Button size="sm" variant="ghost" onClick={dismissSuggestion}>
                      Not now
                    </Button>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={dismissSuggestion}
                  aria-label="Dismiss suggestion"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            {/* Reorder mode UI */}
            {reorderMode && (
              <div className="mb-4 rounded-2xl border border-primary/40 bg-card p-4 shadow-soft">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <h2 className="font-display text-lg font-semibold text-foreground">
                      Reorder aisles
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      Match your store's layout. Saved for all future lists.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={resetReorderToDefault}
                    className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                  >
                    Reset to default
                  </button>
                </div>
                <ul className="space-y-1.5">
                  {draftOrder.map((d, idx) => (
                    <li
                      key={d}
                      className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2"
                    >
                      <GripVertical className="h-4 w-4 text-muted-foreground" />
                      <span className="flex-1 text-sm text-foreground">{d}</span>
                      <button
                        type="button"
                        onClick={() => moveDept(d, -1)}
                        disabled={idx === 0}
                        aria-label={`Move ${d} up`}
                        className="rounded p-1.5 text-muted-foreground transition-smooth hover:bg-background hover:text-foreground disabled:opacity-30"
                      >
                        <ArrowUp className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveDept(d, 1)}
                        disabled={idx === draftOrder.length - 1}
                        aria-label={`Move ${d} down`}
                        className="rounded p-1.5 text-muted-foreground transition-smooth hover:bg-background hover:text-foreground disabled:opacity-30"
                      >
                        <ArrowDown className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="mt-3 flex justify-end gap-2">
                  <Button size="sm" variant="ghost" onClick={cancelReorder}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={saveReorder}>
                    Save order
                  </Button>
                </div>
              </div>
            )}

            {items.length === 0 ? (
              <div className="relative overflow-hidden rounded-2xl border border-dashed border-border bg-card/50 p-10 text-center">
                <EmptyStateMotif variant="shopping" />
                <div className="relative">
                  <h2 className="font-display text-xl font-semibold text-foreground">
                    Your list is empty
                  </h2>
                  <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
                    Add ingredients below, or head back to the menu to regenerate from
                    your recipes.
                  </p>
                  <Button
                    variant="outline"
                    className="mt-5"
                    onClick={() => navigate(`/menus/${id}`)}
                  >
                    Back to menu
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {orderedDepartments.map((dept) => {
                  const deptItems = (grouped.get(dept) || []).sort(
                    (a, b) => a.position - b.position,
                  );
                  if (deptItems.length === 0) return null;
                  const isCollapsed = collapsed.has(dept);
                  return (
                    <div
                      key={dept}
                      className="overflow-hidden rounded-2xl border border-border bg-card shadow-soft"
                    >
                      <button
                        type="button"
                        onClick={() => toggleCollapsed(dept)}
                        className="flex w-full items-center justify-between bg-muted/40 px-4 py-2.5 text-left transition-smooth hover:bg-muted/60"
                      >
                        <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                          {isCollapsed ? (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          )}
                          {dept}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {deptItems.length}
                        </span>
                      </button>
                      {!isCollapsed && (
                        <ul className="divide-y divide-border">
                          {deptItems.map((it, idx) => (
                            <ItemRow
                              key={it.id}
                              item={it}
                              isEditing={editingId === it.id}
                              editName={editName}
                              editQty={editQty}
                              onEditName={setEditName}
                              onEditQty={setEditQty}
                              onStartEdit={() => startEdit(it)}
                              onSaveEdit={saveEdit}
                              onCancelEdit={() => setEditingId(null)}
                              onToggle={() => updateItem(it.id, { checked: !it.checked })}
                              onDelete={() => deleteItem(it.id)}
                              onMove={(d) => moveItem(it.id, d)}
                              onMoveUp={idx > 0 ? () => moveWithin(dept, idx, -1) : undefined}
                              onMoveDown={
                                idx < deptItems.length - 1
                                  ? () => moveWithin(dept, idx, 1)
                                  : undefined
                              }
                              departments={orderedDepartments}
                            />
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add Item */}
            <div className="mt-6 rounded-2xl border border-dashed border-border bg-card/60 p-4">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Add item
              </p>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  placeholder="e.g. 6 eggs"
                  onKeyDown={(e) => e.key === "Enter" && addCustomItem()}
                  className="flex-1"
                />
                <Select
                  value={newItemDept}
                  onValueChange={(v) => setNewItemDept(v as Department)}
                >
                  <SelectTrigger className="sm:w-56">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {orderedDepartments.map((d) => (
                      <SelectItem key={d} value={d}>
                        {d}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={addCustomItem} disabled={!newItemName.trim()}>
                  <Plus className="h-4 w-4" />
                  Add
                </Button>
              </div>
            </div>

            {/* Checked items */}
            {checkedItems.length > 0 && (
              <div className="mt-6 overflow-hidden rounded-2xl border border-border bg-card shadow-soft">
                <button
                  type="button"
                  onClick={() => setShowChecked((v) => !v)}
                  className="flex w-full items-center justify-between bg-muted/40 px-4 py-2.5 text-left transition-smooth hover:bg-muted/60"
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    {showChecked ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                    Checked items
                  </span>
                  <span className="text-xs text-muted-foreground">{checkedItems.length}</span>
                </button>
                {showChecked && (
                  <ul className="divide-y divide-border">
                    {checkedItems.map((it) => (
                      <li
                        key={it.id}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm"
                      >
                        <Checkbox
                          checked
                          onCheckedChange={() => updateItem(it.id, { checked: false })}
                        />
                        <span className="flex-1 truncate text-muted-foreground line-through">
                          {[it.quantity, it.unit, it.name].filter(Boolean).join(" ")}
                        </span>
                        <button
                          type="button"
                          onClick={() => deleteItem(it.id)}
                          className="text-muted-foreground transition-smooth hover:text-destructive"
                          aria-label={`Delete ${it.name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="mt-8 text-center">
              <button
                type="button"
                onClick={handleResetOverrides}
                className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              >
                Reset learned categories
              </button>
            </div>
          </>
        )}
      </section>
    </main>
  );
};

type ItemRowProps = {
  item: ShoppingItem;
  isEditing: boolean;
  editName: string;
  editQty: string;
  onEditName: (v: string) => void;
  onEditQty: (v: string) => void;
  onStartEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
  onMove: (d: Department) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  departments: Department[];
};

const ItemRow = ({
  item, isEditing, editName, editQty, onEditName, onEditQty,
  onStartEdit, onSaveEdit, onCancelEdit, onToggle, onDelete, onMove,
  onMoveUp, onMoveDown, departments,
}: ItemRowProps) => {
  // Move-to-department picker is a bottom sheet rather than a nested
  // submenu — Radix submenus open horizontally and clip off-screen on
  // mobile when the parent menu is right-aligned. The sheet gives full-
  // width tap targets, proper scrolling, and an identical UX on every
  // viewport.
  const [moveOpen, setMoveOpen] = useState(false);

  if (isEditing) {
    return (
      <li className="flex items-center gap-2 px-4 py-2.5">
        <Checkbox checked={item.checked} onCheckedChange={onToggle} />
        <Input
          value={editName}
          onChange={(e) => onEditName(e.target.value)}
          className="h-9 flex-1"
          placeholder="Item name"
          autoFocus
        />
        <Input
          value={editQty}
          onChange={(e) => onEditQty(e.target.value)}
          className="h-9 w-24"
          placeholder="Qty"
          onKeyDown={(e) => {
            if (e.key === "Enter") onSaveEdit();
            if (e.key === "Escape") onCancelEdit();
          }}
        />
        <Button size="icon" variant="outline" onClick={onSaveEdit}>
          <Check className="h-4 w-4" />
        </Button>
      </li>
    );
  }

  const qtyText = [item.quantity, item.unit].filter(Boolean).join(" ");

  return (
    <li className="flex items-center gap-3 px-4 py-2.5 text-sm">
      <Checkbox checked={item.checked} onCheckedChange={onToggle} />
      <div className="flex flex-1 flex-wrap items-baseline gap-x-2 min-w-0">
        <span className="truncate text-foreground">{item.name}</span>
        {qtyText && (
          <span className="shrink-0 text-xs font-medium text-muted-foreground">
            {qtyText}
          </span>
        )}
        {item.note && (
          <span className="shrink-0 text-[11px] italic text-muted-foreground">
            {item.note}
          </span>
        )}
        {/* Low-confidence hint — subtle dot inviting a quick check. */}
        {typeof item.confidence === "number" && item.confidence < 0.6 && !item.custom && (
          <span
            title="Not sure about this category — tap to move if it's wrong"
            aria-label="Low confidence category"
            className="shrink-0 text-[10px] text-muted-foreground/70"
          >
            ?
          </span>
        )}
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`Options for ${item.name}`}
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-smooth hover:bg-muted hover:text-foreground"
          >
            <MoreVertical className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuItem onClick={onStartEdit}>Edit</DropdownMenuItem>
          {onMoveUp && <DropdownMenuItem onClick={onMoveUp}>Move up</DropdownMenuItem>}
          {onMoveDown && <DropdownMenuItem onClick={onMoveDown}>Move down</DropdownMenuItem>}
          <DropdownMenuItem
            onSelect={(e) => {
              // Defer opening the sheet until after the dropdown's own
              // close animation, otherwise focus management fights between
              // the two Radix portals.
              e.preventDefault();
              requestAnimationFrame(() => setMoveOpen(true));
            }}
          >
            Move to department
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onDelete} className="text-destructive">
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Sheet open={moveOpen} onOpenChange={setMoveOpen}>
        <SheetContent
          side="bottom"
          className="max-h-[80vh] overflow-y-auto rounded-t-2xl"
        >
          <SheetHeader className="text-left">
            <SheetTitle>Move "{item.name}" to…</SheetTitle>
          </SheetHeader>
          <ul className="mt-4 space-y-1 pb-4">
            {departments.map((d) => {
              const isCurrent = d === item.department;
              return (
                <li key={d}>
                  <button
                    type="button"
                    disabled={isCurrent}
                    onClick={() => {
                      onMove(d);
                      setMoveOpen(false);
                    }}
                    className={`flex w-full items-center justify-between rounded-lg px-3 py-3 text-left text-sm transition-smooth ${
                      isCurrent
                        ? "cursor-default bg-muted/40 text-muted-foreground"
                        : "hover:bg-muted text-foreground"
                    }`}
                  >
                    <span>{d}</span>
                    {isCurrent && (
                      <span className="text-xs uppercase tracking-wide text-muted-foreground">
                        Current
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </SheetContent>
      </Sheet>
    </li>
  );
};

export default ShoppingList;
